import { FC, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { Field } from 'react-final-form';
import { Tooltip } from 'react-tooltip';
import { Form as BSForm } from 'react-bootstrap';
import { BsInfoCircleFill } from 'react-icons/bs';
import { ResponsiveValue } from 'styled-system';

import { Box, Theme } from '@qwealth/qcore';
import { BaseKycFlags, selectUserIsAdmin, selectUserIsPortfolioManager, selectUserIsAssociatePM } from '@qwealth/qdata';

import { SignificantChangeToolTip, MeaningfulInteractionToolTip } from 'components/common/Label/ToolTips';

const FIELD_SUBSCRIPTION = { value: true };

interface CheckBoxWithTooltipProps {
  id: string;
  label: string;
  name: string;
  tooltip: JSX.Element;
  isDisabled?: boolean;
  checkedValue?: boolean; // External checked state control
  onChange?: (any) => void;
  input?: any; // React Final Form input prop
}

export const CheckBoxWithTooltip: FC<CheckBoxWithTooltipProps> = ({
  id,
  label,
  name,
  tooltip,
  isDisabled = false,
  input,
  onChange,
  checkedValue,
}): ReactElement => {
  return (
    <Box display="flex" alignItems="center">
      <Tooltip id={id}>{tooltip}</Tooltip>
      <BSForm.Check
        {...input}
        type="checkbox"
        disabled={isDisabled}
        id={name}
        checked={checkedValue ?? input.value}
        onChange={e => {
          return onChange ? onChange(e) : input.onChange(e.target.checked ? 1 : 0);
        }}
        label={label}
      />
      <Box data-tooltip-id={id} ml="small">
        <BsInfoCircleFill />
      </Box>
    </Box>
  );
};

type Margin = ResponsiveValue<'auto' | 'small' | 'none' | 'default' | 'large' | 'largest', Theme> | undefined;

interface KYCFlagsProps {
  kycFlags?: BaseKycFlags | null;
  mt?: Margin;
  mb?: Margin;
}

const KYCFlags: FC<KYCFlagsProps> = ({ kycFlags, mt, mb }): ReactElement => {
  const isUserAdmin = useSelector(selectUserIsAdmin);
  const isUserPortfolioManager = useSelector(selectUserIsPortfolioManager);
  const isUserAssociatePM = useSelector(selectUserIsAssociatePM);
  const userHasPermission = isUserAdmin || isUserPortfolioManager || isUserAssociatePM;

  return (
    <Box mt={mt} mb={mb}>
      <Field name="isSignificantChange" subscription={FIELD_SUBSCRIPTION}>
        {({ input }) => {
          return (
            <CheckBoxWithTooltip
              id="significant_change"
              input={input}
              name="isSignificantChange"
              label="Significant Change"
              tooltip={SignificantChangeToolTip}
              isDisabled={kycFlags?.isSignificantChange === 1}
            />
          );
        }}
      </Field>

      <Field name="isMeaningfulInteraction" subscription={FIELD_SUBSCRIPTION}>
        {({ input }) => (
          <CheckBoxWithTooltip
            id="meaningful_interaction"
            input={input}
            name="isMeaningfulInteraction"
            label="Meaningful Interaction"
            isDisabled={!userHasPermission || kycFlags?.isMeaningfulInteraction === 1}
            tooltip={MeaningfulInteractionToolTip}
          />
        )}
      </Field>

      <Field name="isPortfolioReview" subscription={FIELD_SUBSCRIPTION}>
        {({ input }) => (
          <BSForm.Check
            {...input}
            type="checkbox"
            disabled={!userHasPermission || kycFlags?.isPortfolioReview === 1}
            id="isPortfolioReview"
            checked={input.value}
            onChange={e => input.onChange(e.target.checked ? 1 : 0)}
            label="Portfolio Review"
          />
        )}
      </Field>
    </Box>
  );
};

export default KYCFlags;
